import { useState, useMemo, useContext, useEffect } from "react";
import { ArrowLeft, Send, AtSign, User } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import posthog from "@/lib/posthog";

type Agent = RouterOutputs["agents"]["list"][number];
type RecipientClassification = RouterOutputs["leads"]["classifyRecipient"];

// A username can be short ("ana"), so the old 7-character phone gate would have
// hidden it. The debounce does the job that gate was incidentally doing.
const MIN_TERM_LENGTH = 3;
const TERM_DEBOUNCE_MS = 400;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  agents: Agent[];
  onConversationCreated: (leadId: string) => void;
};

type Step = "search" | "template";

type SelectedTemplate = {
  id: string;
  name: string;
  bodyText: string;
  language: string;
  params: string[];
};

const getAutoFillValue = (paramName: string, name?: string, phone?: string): string => {
  const normalized = paramName.toLowerCase();
  if (["name", "nombre", "customer", "cliente"].includes(normalized)) return name || "";
  if (["phone", "telefono", "celular"].includes(normalized)) return phone || "";
  return "";
};

const extractParams = (bodyText: string): string[] => {
  const matches = bodyText.matchAll(/{{(\w+)}}/g);
  return [...matches].map((m) => m[1]);
};

const renderBodyWithHighlightedParams = (bodyText: string, values: Record<string, string>) => {
  const parts = bodyText.split(/({{(\w+)}})/g);
  const rendered: JSX.Element[] = [];
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];
    if (part.match(/^{{\w+}}$/)) {
      const paramName = parts[i + 1];
      const value = values[paramName];
      rendered.push(
        <span key={i} className={cn("font-bold", value ? "text-green-600" : "text-orange-500")}>
          {value || part}
        </span>
      );
      i += 2;
    } else {
      if (part) {
        rendered.push(<span key={i}>{part}</span>);
      }
      i += 1;
    }
  }

  return <p className="whitespace-pre-wrap text-sm">{rendered}</p>;
};

const NewConversationModal = ({ isOpen, onClose, agents, onConversationCreated }: Props) => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  // Step state
  const [step, setStep] = useState<Step>("search");

  // Step 1: Recipient state — a phone number or a WhatsApp handle. Kept raw:
  // routeLeadSearch on the backend ignores the separators people type, and
  // stripping them here would corrupt a handle.
  const [recipientTerm, setRecipientTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  // Empty means "whatever the line resolves to" — only a deliberate pick is sent.
  const [selectedPipelineId, setSelectedPipelineId] = useState("");
  const [contactName, setContactName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  // Step 2: Template state
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const { data: connectionsData } = trpc.whatsappConnections.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const { data: pipelines } = trpc.pipeline.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const enabledConnections = useMemo(
    () => (connectionsData ?? []).filter((c) => !c.disabled),
    [connectionsData]
  );

  const lineAgents = useMemo(
    () => agents.filter((a) => a.whatsappConnectionId === selectedConnectionId),
    [agents, selectedConnectionId]
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(recipientTerm.trim()), TERM_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [recipientTerm]);

  // Resolve the recipient against leads on the selected line
  const canClassify =
    !!merchantId && !!selectedConnectionId && debouncedTerm.length >= MIN_TERM_LENGTH;
  const { data: classificationData, isLoading: isClassifying } =
    trpc.leads.classifyRecipient.useQuery(
      { merchantId, whatsappConnectionId: selectedConnectionId, term: debouncedTerm },
      { enabled: canClassify }
    );
  const classification: RecipientClassification | undefined = canClassify
    ? classificationData
    : undefined;
  const recipientStatus = classification?.status;
  // Only a handle can be unreachable: Meta has no username → BSUID lookup, so a
  // handle we have never been written to cannot be addressed at all.
  const isUnreachable = recipientStatus === "unreachable";
  const needsAcknowledgment = recipientStatus === "otherEmployee" || recipientStatus === "ai";
  const isExistingLead =
    recipientStatus === "mine" || recipientStatus === "unassigned" || recipientStatus === "ai";
  const willCreateLead = recipientStatus === "new" || recipientStatus === "otherEmployee";

  // A reset creates a fresh lead — carry over the previous lead's name as the default
  useEffect(() => {
    if (classification?.leadName) {
      setContactName((prev) => prev || classification.leadName || "");
    }
  }, [classification?.leadName]);

  const wabaId = useMemo(
    () => connectionsData?.find((c) => c.id === selectedConnectionId)?.wabaId,
    [connectionsData, selectedConnectionId]
  );

  // What the server would pick on its own, mirrored so the field shows the real
  // outcome instead of a blank: the agent that will own the lead, else the
  // line's configured funnel, else the merchant default. Kept in sync with
  // resolveConversationStart on the backend.
  const resolvedPipelineId = useMemo(() => {
    const agent = aiEnabled
      ? (lineAgents.find((a) => a.id === selectedAgentId) ??
        lineAgents.find((a) => a.isPrimary) ??
        lineAgents[0])
      : undefined;
    const connection = connectionsData?.find((c) => c.id === selectedConnectionId);
    return agent?.pipelineId ?? connection?.defaultPipelineId ?? "default";
  }, [aiEnabled, lineAgents, selectedAgentId, connectionsData, selectedConnectionId]);

  // One funnel means there is nothing to choose, and an existing lead already
  // has one — the send goes through sendTemplateMessage, which never sets it.
  const showPipelinePicker = willCreateLead && (pipelines?.length ?? 0) > 1;
  // The resolved id can name a funnel the merchant no longer has (an agent
  // pointing at a deleted pipeline). Never show — or send — an id that is not
  // one of the options.
  const pipelineValue =
    selectedPipelineId ||
    ((pipelines ?? []).some((p) => p.id === resolvedPipelineId)
      ? resolvedPipelineId
      : ((pipelines ?? [])[0]?.id ?? ""));

  // Fetch templates
  const { data: templatesData, isLoading: isLoadingTemplates } =
    trpc.whatsappTemplates.list.useQuery(
      { merchantId },
      { enabled: !!merchantId && step === "template" }
    );

  const templates = useMemo(() => {
    if (!templatesData?.templates || !wabaId) return [];
    return templatesData.templates.filter(
      (t) =>
        t.wabaId === wabaId &&
        t.config.category !== "AUTHENTICATION" &&
        t.bodyPreview &&
        t.status === "APPROVED"
    );
  }, [templatesData?.templates, wabaId]);

  // Mutations
  const createMutation = trpc.conversations.createConversationAndSendTemplate.useMutation();
  const sendTemplateMutation = trpc.conversations.sendTemplateMessage.useMutation();
  const isSending = createMutation.isLoading || sendTemplateMutation.isLoading;

  const handleConnectionChange = (connectionId: string) => {
    setSelectedConnectionId(connectionId);
    setSelectedAgentId("");
    setAiEnabled(true);
    setSelectedPipelineId("");
    setAcknowledged(false);
  };

  const canProceedToTemplate = useMemo(() => {
    if (!selectedConnectionId) return false;
    if (!recipientStatus || isUnreachable) return false;
    if (needsAcknowledgment && !acknowledged) return false;
    if (willCreateLead && aiEnabled && lineAgents.length > 1 && !selectedAgentId) return false;
    return true;
  }, [
    selectedConnectionId,
    recipientStatus,
    isUnreachable,
    needsAcknowledgment,
    acknowledged,
    willCreateLead,
    aiEnabled,
    lineAgents,
    selectedAgentId,
  ]);

  const handleProceedToTemplate = () => {
    setStep("template");
  };

  const handleBackToSearch = () => {
    setStep("search");
    setSelectedTemplate(null);
    setParamValues({});
  };

  const handleSelectTemplate = (template: (typeof templates)[number]) => {
    const bodyText = template.bodyPreview || "";
    const params = extractParams(bodyText);

    setSelectedTemplate({
      id: template.id,
      name: template.name,
      bodyText,
      language: template.language,
      params,
    });

    const autoFilled: Record<string, string> = {};
    const name = classification?.leadName || contactName;
    // Only fill a {{phone}} param when the term actually is one — a phoneless
    // lead has no number, and pasting its handle there would send the customer
    // their own BSUID.
    const phone = classification?.matched === "phone" ? recipientTerm.trim() : "";
    params.forEach((param) => {
      autoFilled[param] = getAutoFillValue(param, name, phone);
    });
    setParamValues(autoFilled);
  };

  const allParamsFilled = useMemo(() => {
    if (!selectedTemplate) return false;
    return selectedTemplate.params.every((param) => (paramValues[param] || "").trim().length > 0);
  }, [selectedTemplate, paramValues]);

  const handleSendSuccess = (leadId: string) => {
    posthog.capture("new conversation started", {
      merchant_id: merchantId,
      template_id: selectedTemplate?.id,
      template_name: selectedTemplate?.name,
      recipient_status: recipientStatus,
    });
    toast.success("Conversacion creada", {
      description: "La plantilla fue enviada exitosamente.",
      duration: 3000,
    });
    onConversationCreated(leadId);
    handleReset();
    onClose();
  };

  const handleSendError = (error: { message: string; data?: { code?: string } | null }) => {
    if (error.data?.code === "CONFLICT") {
      utils.leads.classifyRecipient.invalidate();
      setStep("search");
      setSelectedTemplate(null);
      setParamValues({});
      setAcknowledged(false);
      toast.error("El estado de este contacto cambió", {
        description: "Revisa la información actualizada e intenta de nuevo.",
        duration: 5000,
      });
      return;
    }
    toast.error("Error al crear conversacion", {
      description: error.message,
      duration: 5000,
    });
  };

  const handleSend = () => {
    // canProceedToTemplate already blocks this, but a refetch can land here
    // while step 2 is open — and without the guard an unreachable handle would
    // fall through to the phone branch and fail as "Invalid phone number".
    if (!selectedTemplate || !recipientStatus || isUnreachable) return;

    if (isExistingLead && classification?.leadId) {
      const leadId = classification.leadId;
      sendTemplateMutation.mutate(
        { merchantId, leadId, templateId: selectedTemplate.id, params: paramValues },
        { onSuccess: () => handleSendSuccess(leadId), onError: handleSendError }
      );
      return;
    }

    createMutation.mutate(
      {
        merchantId,
        // A handle-identified recipient carries the id recovered from the lead
        // it was matched against; there is no other way to obtain one.
        ...(classification?.recipient
          ? {
              whatsappUserId: classification.recipient.whatsappUserId,
              ...(classification.recipient.whatsappUsername
                ? { whatsappUsername: classification.recipient.whatsappUsername }
                : {}),
              ...(classification.reengagedFromLeadId
                ? { reengagedFromLeadId: classification.reengagedFromLeadId }
                : {}),
            }
          : { phone: recipientTerm.replace(/[\s+]/g, "") }),
        whatsappConnectionId: selectedConnectionId,
        aiActive: aiEnabled,
        agentId: aiEnabled && lineAgents.length > 1 ? selectedAgentId : undefined,
        pipelineId: showPipelinePicker ? pipelineValue : undefined,
        existingLeadAction: recipientStatus === "otherEmployee" ? "resetAndReassign" : undefined,
        templateId: selectedTemplate.id,
        params: paramValues,
        name: contactName || undefined,
      },
      {
        onSuccess: (result: { leadId: string }) => handleSendSuccess(result.leadId),
        onError: handleSendError,
      }
    );
  };

  const handleReset = () => {
    setStep("search");
    setRecipientTerm("");
    setDebouncedTerm("");
    setSelectedAgentId("");
    setAiEnabled(true);
    setSelectedConnectionId("");
    setSelectedPipelineId("");
    setContactName("");
    setAcknowledged(false);
    setSelectedTemplate(null);
    setParamValues({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const getStatusBadge = (status: string) => {
    if (status === "APPROVED") return { color: "green", label: "Aprobada" };
    if (status === "REJECTED") return { color: "red", label: "Rechazada" };
    return { color: "yellow", label: "Pendiente" };
  };

  const renderClassificationBanner = () => {
    if (!canClassify) return null;
    if (isClassifying) {
      return (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner size="sm" />
          Verificando el contacto en esta línea...
        </div>
      );
    }
    // Deliberately generic: a merchant who typed a name rather than a handle
    // lands here too, and the message is true for them as well.
    if (isUnreachable) {
      return (
        <Alert status="warning">
          <p className="text-sm">
            No encontramos ningún lead con ese usuario de WhatsApp. Para iniciar una conversación
            nueva necesitas su número de teléfono.
          </p>
        </Alert>
      );
    }

    if (!recipientStatus || recipientStatus === "new") return null;

    if (recipientStatus === "mine" || recipientStatus === "unassigned") {
      return (
        <Alert status="success">
          <p className="text-sm">
            Ya tienes una conversación activa con este contacto en esta línea
            {classification?.leadName ? ` (${classification.leadName})` : ""}. El mensaje se
            enviará ahí.
          </p>
        </Alert>
      );
    }

    const warningText =
      recipientStatus === "otherEmployee"
        ? `Este contacto está siendo atendido por ${classification?.employeeName || "otro asesor"} en esta línea. Si continúas, esa conversación se cerrará y se creará una nueva.`
        : "El agente IA está atendiendo esta conversación. Si continúas, tomarás el control y la IA se desactivará.";

    return (
      <Alert status="warning">
        <p className="text-sm">{warningText}</p>
        <label className="mt-2 flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={(checked) => setAcknowledged(checked === true)}
          />
          Entiendo, continuar
        </label>
      </Alert>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base">
            {step === "search" ? "Nueva conversación" : "Seleccionar plantilla"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {step === "search" ? (
            <div className="flex flex-col gap-4">
              {/* Recipient: a phone number or a WhatsApp handle */}
              <div>
                <p className="mb-1 text-xs font-medium text-gray-600">
                  Teléfono o usuario de WhatsApp
                </p>
                <div className="relative">
                  <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                    <AtSign className="size-4 text-gray-400" />
                  </div>
                  <input
                    className="h-8 w-full rounded-md border border-gray-200 bg-white pl-10 pr-3 text-base placeholder:text-gray-400 focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
                    placeholder="Ej: 573001234567 o @usuario"
                    value={recipientTerm}
                    onChange={(e) => {
                      setRecipientTerm(e.target.value);
                      setAcknowledged(false);
                    }}
                  />
                </div>
              </div>

              {/* WhatsApp line */}
              <div>
                <p className="mb-1 text-xs font-medium text-gray-600">
                  Línea de WhatsApp <span className="text-red-500">*</span>
                </p>
                <select
                  className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary"
                  value={selectedConnectionId}
                  onChange={(e) => handleConnectionChange(e.target.value)}
                >
                  <option value="">Selecciona una linea</option>
                  {enabledConnections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.phoneNumber || c.phoneNumberId}
                    </option>
                  ))}
                </select>
                {enabledConnections.length === 0 && (
                  <p className="mt-1 text-2xs text-gray-500">
                    No hay líneas de WhatsApp disponibles.
                  </p>
                )}
              </div>

              {renderClassificationBanner()}

              {/* New-lead fields (new number, or reset of another advisor's lead) */}
              {willCreateLead && (
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-600">
                      Nombre del contacto (opcional)
                    </p>
                    <div className="relative">
                      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                        <User className="size-4 text-gray-400" />
                      </div>
                      <input
                        className="h-8 w-full rounded-md border border-gray-200 bg-white pl-10 pr-3 text-base placeholder:text-gray-400 focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
                        placeholder="Nombre del contacto"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                      />
                    </div>
                  </div>

                  {lineAgents.length > 0 ? (
                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-600">Agente IA</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setAiEnabled(true)}
                          className={cn(
                            "min-h-11 flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                            aiEnabled
                              ? "border-palomma-primary bg-purple-50 font-medium text-palomma-primary"
                              : "border-gray-200 text-gray-600"
                          )}
                        >
                          Activo
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiEnabled(false)}
                          className={cn(
                            "min-h-11 flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                            !aiEnabled
                              ? "border-palomma-primary bg-purple-50 font-medium text-palomma-primary"
                              : "border-gray-200 text-gray-600"
                          )}
                        >
                          Inactivo
                        </button>
                      </div>
                      {!aiEnabled && (
                        <p className="mt-1 text-2xs text-gray-500">El lead se asignará a ti.</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-2xs text-gray-500">
                      Esta línea no tiene agente IA — la conversación será asignada a ti.
                    </p>
                  )}

                  {aiEnabled && lineAgents.length > 1 && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-600">
                        Agente IA <span className="text-red-500">*</span>
                      </p>
                      <select
                        className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary"
                        value={selectedAgentId}
                        onChange={(e) => setSelectedAgentId(e.target.value)}
                      >
                        <option value="">Selecciona un agente</option>
                        {lineAgents.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.agentName || a.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {showPipelinePicker && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-600">Embudo</p>
                      <select
                        className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary"
                        value={pipelineValue}
                        onChange={(e) => setSelectedPipelineId(e.target.value)}
                      >
                        {(pipelines ?? []).map((pipeline) => (
                          <option key={pipeline.id} value={pipeline.id}>
                            {pipeline.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : selectedTemplate ? (
            /* Step 2b: Fill template params */
            <div className="flex flex-col gap-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedTemplate(null);
                  setParamValues({});
                }}
                className="self-start"
              >
                <ArrowLeft className="size-4 mr-1" />
                Volver a plantillas
              </Button>

              <div className="flex items-center justify-between">
                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                  {selectedTemplate.name}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-2xs font-medium text-gray-800">
                  {selectedTemplate.language.toUpperCase()}
                </span>
              </div>

              <div className="rounded-md bg-gray-50 p-3 text-sm">
                {renderBodyWithHighlightedParams(selectedTemplate.bodyText, paramValues)}
              </div>

              {selectedTemplate.params.map((param) => (
                <div key={param}>
                  <p className="mb-1 text-xs font-medium text-gray-600">{param}</p>
                  <input
                    className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
                    value={paramValues[param] || ""}
                    onChange={(e) =>
                      setParamValues((prev) => ({ ...prev, [param]: e.target.value }))
                    }
                    placeholder={`Valor para {{${param}}}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            /* Step 2a: Select template */
            <div className="flex flex-col gap-3">
              <Button size="sm" variant="ghost" onClick={handleBackToSearch} className="self-start">
                <ArrowLeft className="size-4 mr-1" />
                Volver
              </Button>

              <Alert status="info">
                <p className="text-sm">
                  Selecciona una plantilla para iniciar la conversacion. El cliente debe responder
                  para habilitar mensajes libres.
                </p>
              </Alert>

              <p className="text-sm font-medium">Plantillas disponibles</p>

              {isLoadingTemplates ? (
                <div className="flex justify-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : templates.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  No hay plantillas aprobadas disponibles.
                </p>
              ) : (
                <div className="md:max-h-[300px] md:overflow-y-auto">
                  <div className="flex flex-col gap-2">
                    {templates.map((template) => {
                      const badge = getStatusBadge(template.status);
                      return (
                        <div
                          key={template.id}
                          className="cursor-pointer rounded-md border border-gray-200 p-3 transition-all duration-150 hover:border-purple-300 hover:bg-purple-50"
                          onClick={() => handleSelectTemplate(template)}
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <p className="truncate text-sm font-medium">{template.name}</p>
                            <div className="flex items-center gap-1">
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-2xs font-medium",
                                  badge.color === "green" && "bg-green-100 text-green-800",
                                  badge.color === "red" && "bg-red-100 text-red-800",
                                  badge.color === "yellow" && "bg-yellow-100 text-yellow-800"
                                )}
                              >
                                {badge.label}
                              </span>
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-2xs font-medium text-gray-800">
                                {template.language.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <p className="line-clamp-2 text-xs text-gray-500">
                            {template.bodyPreview || ""}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {step === "search" ? (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="main"
                onClick={handleProceedToTemplate}
                disabled={!canProceedToTemplate}
              >
                Siguiente
              </Button>
            </div>
          ) : selectedTemplate ? (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                size="sm"
                className="rounded-full bg-green-600 text-white hover:bg-green-700"
                onClick={handleSend}
                disabled={!allParamsFilled || isSending}
              >
                {isSending ? (
                  <>
                    <Spinner size="sm" className="text-white" />
                    Enviando
                  </>
                ) : (
                  <>
                    <Send className="size-4" />
                    Enviar plantilla
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationModal;
